/**
 * ============================================
 * API CLIENT - Cliente HTTP para el Backend
 * ============================================
 *
 * Este archivo configura Axios para comunicarse con el backend Django.
 * Incluye:
 * - URL base del servidor
 * - Interceptores para agregar token automáticamente
 * - Manejo global de errores
 *
 * USO:
 * import { api } from '@/lib/api';
 * const { data } = await api.get('/pacientes/');
 */

import axios from "axios";

/**
 * URL base del backend
 *
 * El backend Django expone la API en /api/v1/
 * En desarrollo: Vite proxy redirige /api a localhost:8000
 * En producción: Se configura via variable de entorno
 *
 * IMPORTANTE: Todas las rutas del backend están bajo /api/v1/
 * Ejemplo: /api/v1/auth/login/, /api/v1/auth/me/
 */
const BASE_URL = import.meta.env.VITE_API_URL || "/api/v1";

/**
 * Instancia de Axios configurada
 *
 * Crear una instancia permite tener configuración específica
 * sin afectar otras peticiones que puedas hacer.
 */
export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000, // 30 segundos máximo por petición
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * INTERCEPTOR DE REQUEST
 *
 * Se ejecuta ANTES de cada petición.
 * Aquí agregamos el token de autenticación automáticamente.
 */
api.interceptors.request.use(
  (config) => {
    // Obtener token del localStorage
    const token = localStorage.getItem("access_token");

    // Si hay token, agregarlo al header Authorization
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    // Error antes de enviar la petición
    return Promise.reject(error);
  }
);

/**
 * MUTEX PARA TOKEN REFRESH CONCURRENTE
 *
 * Cuando múltiples peticiones reciben 401 simultáneamente,
 * solo la primera ejecuta el refresh. Las demás se encolan
 * y se reintentan con el nuevo token una vez completado.
 */
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) {
      resolve(token);
    } else {
      reject(error);
    }
  });
  failedQueue = [];
}

/**
 * INTERCEPTOR DE RESPONSE
 *
 * Se ejecuta DESPUÉS de cada respuesta.
 * Aquí manejamos errores globales como token expirado.
 *
 * NOTA: No usamos window.location.href porque causa problemas con React.
 * En su lugar, limpiamos los tokens y dejamos que ProtectedRoute
 * maneje la redirección a /login.
 */
api.interceptors.response.use(
  // Respuesta exitosa (2xx) - solo retornar
  (response) => response,

  // Error en la respuesta
  async (error) => {
    const originalRequest = error.config;

    // Si el error es 401 (no autorizado) y no es un retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      // No hay refresh token - limpiar todo
      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user");
        return Promise.reject(error);
      }

      // Si ya hay un refresh en curso, encolar esta petición
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      // Primera petición 401 - ejecutar refresh
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(`${BASE_URL}/auth/refresh/`, {
          refresh: refreshToken,
        });

        const { access } = response.data;
        localStorage.setItem("access_token", access);

        // Resolver todas las peticiones encoladas con el nuevo token
        processQueue(null, access);

        // Reintentar la petición original
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh falló - rechazar todas las peticiones encoladas
        processQueue(refreshError, null);

        // Limpiar tokens - ProtectedRoute redirigirá a /login
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user");
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);
